/**
 * OAuth consent screen (de).
 *
 * Every literal `@` is escaped as {'@'}. vue-i18n reads a bare `@` as the
 * start of a linked message, throws when the message is rendered, and blanks
 * the whole enclosing subtree with nothing in the browser console: it looks
 * like a styling bug, not a translation one. tests/locales/messageSyntax.spec.ts
 * compiles every message in here.
 */
export const oauth = {
  consent: {
    pageTitle: 'Zugriff erlauben',
    heading: 'Zugriff auf dein ITSH-Konto',
    intro: '„{client}“ möchte in deinem Namen auf dein ITSH-Konto zugreifen.',
    loading: 'Anfrage wird geladen',
    expiresAt: 'Diese Anfrage läuft am {date} ab.',
    trust: {
      title: 'Wir haben diese Anwendung nicht geprüft',
      body: 'Jede Anwendung kann sich hier selbst registrieren, Namen und Adresse frei wählen und sich als jemand anderes ausgeben. Wir prüfen das nicht. Erlaube den Zugriff nur, wenn du diese Verbindung selbst gerade gestartet hast.',
      levelLabel: 'Einstufung',
      levels: {
        unverified: 'Nicht geprüft',
        verified: 'Geprüft',
        first_party: 'ITSH'
      }
    },
    client: {
      sectionTitle: 'Anwendung',
      name: 'Name',
      host: 'Verifizierter Host',
      hostNone: 'Kein verifizierter Host',
      hostNoneHint: 'Diese Anwendung hat sich selbst registriert. Ihr Name sagt nichts darüber aus, wer sie betreibt.',
      hostPlatformHint: 'Diese Adresse liegt auf der ITSH-Plattform, gehört aber nicht zu uns: jeder Kunde kann dort eine Anwendung veröffentlichen.',
      clientId: 'Client-ID',
      resources: 'Wofür der Zugriff gilt',
      resourcesHint: 'Nur diese Schnittstellen akzeptieren den Zugriff. Überall sonst ist er wertlos.',
      redirect: 'Weiterleitung nach der Entscheidung',
      redirectHint: 'Genau an diese Adresse schicken wir dich zurück, samt Zugriffscode. Prüfe sie Zeichen für Zeichen.'
    },
    scopes: {
      sectionTitle: 'Diese Berechtigungen fragt die Anwendung an',
      hint: 'Du kannst einzelne Berechtigungen abwählen. Was du abwählst, bekommt die Anwendung nicht.',
      none: 'Die Anwendung fragt keine Berechtigung an, die wir kennen.',
      noneSelected: 'Du hast alles abgewählt. Damit lehnst du die Anfrage ab.',
      withheld: 'Die Mail-Berechtigungen dieser Anfrage ({scopes}) können wir nicht erteilen: Für dein Konto ist vermerkt, dass du keinen Auftragsverarbeitungsvertrag (AVV) brauchst, weil du uns keine personenbezogenen Daten Dritter anvertraust. Ohne AVV haben wir keine Weisung, Postfachdaten an eine Anwendung weiterzugeben. Akzeptiere den AVV im Kundenportal unter „AVV“, dann kannst du diese Berechtigungen erteilen.',
      unknownTitle: 'Unbekannte Berechtigung',
      unknownDescription: 'Diese Berechtigung kennen wir in dieser Version noch nicht und können dir nicht sagen, was sie erlaubt.',
      billingPurchaseNote: 'Zum Kaufen braucht die Anwendung zusätzlich die Berechtigung "Abrechnung: Kaufen". Ohne sie kauft sie nichts, egal was du hier auswählst.',
      riskConfirm: 'Mir ist klar, dass die kritischen Berechtigungen oben auch dann weiterwirken, wenn ich den Zugriff später entziehe.'
    },
    risk: {
      low: 'Liest nur',
      medium: 'Ändert Daten',
      high: 'Kritisch'
    },
    areas: {
      account: 'Konto',
      billing: 'Abrechnung',
      dns: 'DNS',
      domain: 'Domains',
      handle: 'Domain-Kontakte',
      hosting: 'Webhosting',
      k8s: 'Kubernetes',
      mail: 'E-Mail',
      registry: 'Container-Registry',
      storage: 'Object Storage',
      vserver: 'VServer'
    },
    levels: {
      read: 'Lesen',
      write: 'Ändern',
      delete: 'Löschen',
      purchase: 'Kaufen',
      credentials: 'Zugangsdaten',
      exec: 'Befehle ausführen',
      console: 'Konsole',
      authcode: 'Auth-Code',
      transfer: 'Transfer'
    },
    descriptions: {
      account: {
        read: 'Liest dein Profil: Name, Anschrift und Kontoeinstellungen.',
        write: 'Ändert dein Profil, zum Beispiel deine Anschrift oder deine Kontaktdaten.',
        credentials: "Legt SSH-Schlüssel in deinem Konto an und entfernt sie, zum Beispiel deinname{'@'}laptop."
      },
      billing: {
        read: 'Liest Rechnungen, Abos und offene Beträge.',
        write: 'Ändert Abrechnungsdaten, zum Beispiel dein Abo oder die Rechnungsanschrift.',
        purchase: 'Erlaubt überhaupt erst, Geld auszugeben. Wie viel, entscheidet dein Ausgabenlimit weiter unten.'
      },
      dns: {
        read: 'Liest deine DNS-Zonen und alle Einträge darin.',
        write: 'Legt DNS-Einträge an, ändert und löscht sie. Damit steuert die Anwendung, wohin deine Domains und deine E-Mail zeigen. Das umfasst die Einträge, die festlegen, welche Server deine E-Mail empfangen (MX) und welche in deinem Namen E-Mails senden dürfen (SPF, DKIM, DMARC), und die, über die sich Zertifikate für deine Domains ausstellen lassen (CAA und _acme-challenge).',
        credentials: 'Erstellt DynDNS-Tokens für deine Einträge.'
      },
      domain: {
        read: 'Liest deine Domains, Laufzeiten und Nameserver.',
        write: 'Ändert Domain-Einstellungen, zum Beispiel Nameserver oder die automatische Verlängerung.',
        delete: 'Kündigt Domains aus deinem Konto.',
        authcode: 'Fordert Auth-Codes für deine Domains an.',
        transfer: 'Lehnt Anfragen ab, deine Domains aus deinem Konto zu übertragen.',
        purchase: 'Registriert neue Domains auf deine Rechnung.'
      },
      handle: {
        read: 'Liest deine Domain-Kontakte und ob sie bestätigt sind, mit Name, Anschrift, E-Mail-Adresse und Telefonnummer nur bei deinen eigenen Standardkontakten.',
        write: "Legt Domain-Kontakte an und ändert sie; bei einem Kontakt, der Inhaber- oder Admin-Kontakt einer deiner Domains ist, nur Telefon- und Faxnummer. Schickt einem Kontakt einen Bestätigungscode per E-Mail oder SMS, zum Beispiel an inhaber{'@'}deine-domain.de, und bestätigt ihn mit dem Code, den du weitergibst.",
        delete: 'Löscht Domain-Kontakte aus deinem Konto.'
      },
      hosting: {
        read: 'Liest deine Webhosting-Sites, ihre Domains und ihren Status.',
        write: 'Ändert deine Webhosting-Sites, zum Beispiel Domains, PHP-Version oder Cronjobs.',
        delete: 'Löscht Webhosting-Sites samt der darauf liegenden Daten.',
        credentials: 'Erstellt SFTP- und Datenbank-Zugangsdaten für deine Sites, liest vorhandene Datenbank-Zugangsdaten und lädt zusammen mit „Webhosting: Lesen“ Backups deiner Sites herunter.',
        exec: 'Führt beliebige Befehle in deinem Hosting-Container aus.',
        purchase: 'Bucht Webhosting-Pakete auf deine Rechnung.'
      },
      k8s: {
        read: 'Liest deine Namespaces, die Workloads darin und deren Verbrauch.',
        write: 'Legt Ressourcen in deinen Namespaces an, ändert und entfernt sie.',
        credentials: 'Erstellt Kubeconfigs für deine Namespaces.',
        purchase: 'Bucht zusätzliche Kubernetes-Kontingente auf deine Rechnung.'
      },
      mail: {
        read: "Liest deine Postfächer und ihre Einstellungen, zum Beispiel info{'@'}deine-domain.de.",
        write: "Legt Postfächer, Aliasse, Weiterleitungen, Abwesenheitsnotizen und das Catch-All an und ändert sie, zum Beispiel kontakt{'@'}deine-domain.de, und kann Adressen abschalten. Eine Weiterleitung darf auch aus deinen Domains hinausführen, an dich gerichtete E-Mails können also an jede beliebige Adresse gehen.",
        delete: 'Löscht Postfächer samt aller E-Mails, die darin liegen, sowie Aliasse und Weiterleitungen, und schaltet das Catch-All ab.',
        credentials: 'Setzt die Passwörter deiner Postfächer neu.',
        purchase: 'Bucht E-Mail-Pakete auf deine Rechnung.'
      },
      registry: {
        read: 'Liest deine Container-Registry und die Images darin.',
        write: 'Lädt Images in deine Registry und überschreibt vorhandene.',
        delete: 'Löscht Images und ganze Repositories aus deiner Registry.',
        credentials: 'Erstellt Zugangsdaten für deine Registry.',
        purchase: 'Bucht zusätzliche Registry-Kontingente auf deine Rechnung.'
      },
      storage: {
        read: 'Liest deine Buckets und die Objekte darin.',
        write: 'Legt Objekte in deinen Buckets an und überschreibt vorhandene.',
        delete: 'Löscht Objekte und ganze Buckets.',
        credentials: 'Erstellt S3-Zugangsschlüssel für deine Buckets.',
        purchase: 'Bucht zusätzlichen Speicher auf deine Rechnung.'
      },
      vserver: {
        read: 'Liest deine VServer, ihren Status und ihren Verbrauch.',
        write: 'Startet, stoppt und verändert deine VServer.',
        delete: 'Löscht VServer samt ihrer Festplatten.',
        credentials: 'Setzt Root-Passwörter und hinterlegt SSH-Schlüssel auf deinen VServern.',
        console: 'Öffnet die Notfall-Konsole und tippt darin wie an einer angeschlossenen Tastatur.',
        purchase: 'Bestellt neue VServer auf deine Rechnung.'
      }
    },
    consequences: {
      delete: 'Gelöschtes holt niemand zurück, auch nicht, wenn du den Zugriff danach entziehst.',
      purchase: 'Das kostet echtes Geld und wird über deine hinterlegte Zahlungsart abgerechnet.',
      credentials: 'Einmal ausgestellte Zugangsdaten funktionieren weiter, auch nachdem du den Zugriff entzogen hast. Du musst sie einzeln zurückziehen.',
      exec: 'Wer Befehle ausführen darf, kommt an alles heran, was auf dem Container liegt.',
      console: 'Über die Konsole ist das laufende System vollständig zugänglich, unabhängig von diesem Zugriff.',
      authcode: 'Wer einen Auth-Code hat, kann die Domain zu einem anderen Anbieter umziehen.',
      transfer: 'Ein abgelehnter Transfer muss neu angefragt werden.'
    },
    expiry: {
      sectionTitle: 'Wie lange soll der Zugriff gelten?',
      hint: 'Danach muss die Anwendung dich erneut fragen. Du kannst den Zugriff jederzeit vorher entziehen.',
      days7: '7 Tage',
      days30: '30 Tage',
      days90: '90 Tage',
      never: 'Unbegrenzt',
      neverHint: 'Unbegrenzt heißt: bis du den Zugriff selbst entziehst.'
    },
    spend: {
      sectionTitle: 'Ausgabenlimit',
      label: 'Limit in Euro',
      hint: 'So viel darf diese Anwendung insgesamt auf deine Rechnung ausgeben. Null heißt: gar nichts.',
      zeroNote: 'Bei null kauft die Anwendung nichts. Das ist die Voreinstellung.',
      confirm: 'Mir ist klar, dass diese Anwendung bis zu {amount} auf meine Rechnung ausgeben darf.',
      invalid: 'Bitte gib einen Betrag von 0 oder mehr ein.'
    },
    transfer: {
      sectionTitle: 'Weitergabe und Drittländer',
      recipients: 'Was diese Anwendung mit den erlaubten Berechtigungen abruft, senden wir an sie. Wohin es von dort geht, entscheiden die Anwendung und ihre Einstellungen: an ihren Anbieter, an den Anbieter des KI-Modells, an beide, oder an niemanden, wenn alles auf deinen eigenen Geräten läuft. Wir können das nicht erkennen.',
      policyUri: 'Datenschutzerklärung der Anwendung:',
      tosUri: 'Nutzungsbedingungen der Anwendung:',
      urisHint: 'Beide Adressen stammen von der Anwendung selbst und sind von uns nicht geprüft; wer die Daten erhält und in welchem Land, solltest du dort nachlesen.',
      basis: 'Sitzt ein Empfänger außerhalb des EWR, zum Beispiel in den USA, gilt: Wir haben mit ihm keinen Vertrag und keine Garantien nach Art. 46 DSGVO; ob für sein Land ein Angemessenheitsbeschluss der EU-Kommission gilt, hängt von der Anwendung ab. Ohne einen solchen Beschluss stützen wir die Übermittlung deiner Daten auf deine ausdrückliche Einwilligung (Art. 49 Abs. 1 lit. a DSGVO).',
      risks: 'Die Risiken: In dem Land gibt es möglicherweise keine Aufsichtsbehörde, keine der DSGVO entsprechenden Verarbeitungsgrundsätze und keine durchsetzbaren Betroffenenrechte, und wir haben auf die Verarbeitung dort keinen Einfluss.',
      scope: 'Die Einwilligung gilt für deine eigenen Daten. Daten anderer Personen gelangen nur eingeschränkt an den Empfänger: von fremden Domain-Kontakten nur Kennung, Typ, Land, Prüfstatus und bei Organisationen der Firmenname, mit Mail-Berechtigungen etwa Postfachnamen; prüfe, ob du das darfst.',
      withdraw: 'Du kannst die Einwilligung jederzeit widerrufen, indem du die Verbindung unter „Verbundene Anwendungen“ trennst. Der Widerruf ändert nichts an der Rechtmäßigkeit dessen, was bis dahin übermittelt wurde, und holt bereits übermittelte Daten nicht zurück.',
      details: 'Einzelheiten stehen in der Datenschutzerklärung unter {section}. Zeitpunkt, IP-Adresse und Browserkennung deiner Entscheidung speichern wir als Nachweis.',
      detailsLink: '„Verbundene Anwendungen und KI-Agenten“',
      confirm: 'Soweit die Daten, die diese Anwendung abruft, an einen Empfänger außerhalb des EWR gelangen, willige ich in diese Übermittlung ausdrücklich ein. Die Risiken habe ich zur Kenntnis genommen.'
    },
    avv: {
      sectionTitle: 'Erst den Auftragsverarbeitungsvertrag akzeptieren',
      body: 'Dein Konto enthält Produkte, bei denen wir personenbezogene Daten in deinem Auftrag verarbeiten, etwa Webhosting oder E-Mail. Bevor eine Anwendung Zugriff bekommt, muss die aktuelle Fassung des Auftragsverarbeitungsvertrags (AVV, Art. 28 DSGVO) akzeptiert sein. Lies ihn, akzeptiere ihn und erlaube den Zugriff dann noch einmal. Die Anfrage bleibt bis zu der oben genannten Zeit offen.',
      openPdf: 'Vertrag in neuem Tab öffnen',
      pdfUnavailable: 'Der Vertrag konnte nicht angezeigt werden. Du findest ihn auch im Control Panel unter „AVV“.',
      accepted: 'Vertrag akzeptiert. Du kannst den Zugriff jetzt erlauben.'
    },
    actions: {
      approve: 'Zugriff erlauben',
      deny: 'Ablehnen',
      working: 'Einen Moment'
    },
    errors: {
      missingRequest: 'Dieser Seite fehlt die Anfrage-Kennung. Starte die Verbindung in der Anwendung noch einmal.',
      unusable: 'Diese Anfrage ist abgelaufen oder wurde schon beantwortet. Starte die Verbindung in der Anwendung noch einmal.',
      accountHolder: 'Diese Entscheidung kann nur die Kontoinhaberin oder der Kontoinhaber selbst treffen, in der eigenen Sitzung. Aus einer übernommenen oder gewechselten Sitzung geht das nicht.',
      loadFailed: 'Die Anfrage konnte nicht geladen werden.',
      rateLimited: 'Zu viele Versuche in kurzer Zeit. Warte eine Minute und versuche es dann noch einmal.',
      submitFailed: 'Deine Entscheidung konnte nicht gespeichert werden. Versuche es noch einmal.',
      decisionRecorded: 'Deine Entscheidung wurde gespeichert, aber die Anwendung konnte nicht zurückgerufen werden. Geh zu ihr zurück und starte die Verbindung noch einmal. Den Zugriff siehst du jederzeit unter „Verbundene Anwendungen“ und kannst ihn dort auch entziehen.',
      agentAccess: 'Für dein Konto ist das Verbinden von Anwendungen noch nicht freigeschaltet. Wende dich an den ITSH-Support, wenn du es nutzen möchtest.',
      avvUnavailable: 'Wir konnten deinen Auftragsverarbeitungsvertrag gerade nicht prüfen. Versuche es gleich noch einmal.',
      toConnections: 'Zu den verbundenen Anwendungen',
      retry: 'Erneut versuchen',
      toDashboard: 'Zum Control Panel'
    }
  }
} as const
